import React, { Component } from "react";
import { Form, Dropdown, Table, Image, Modal, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { PiEye, PiInfo, PiWarning } from "react-icons/pi";
import Pagination from "react-js-pagination";
import { toast } from "react-toastify";
import axios from "../../../axios";
import axiosLib from "axios";
import apiError from "../../../error";
import swalAlert from "../../../swalFunction";
import { getDate } from "../../../function";
import ConfiguredModal from "../configured-modal";
import ReplyModal from "../../campaigns/results/reply-modal";
import simulationandtrain from "../../../assets/create_campaign/simulationandtrain.png";
import "./steps.css";

const getCancelSource = () => {
	const CancelToken = axiosLib.CancelToken;
	return CancelToken ? CancelToken.source() : null;
};

const DEFAULT_COURSE_OPTIONS = {
	isLectureSwitching: false,
	isVideoSeeking: false,
	isSkipQuestions: false,
	isQuizBeforeVideo: false,
	isQuizFailReminder: true,
	isQuizRandomizer: false,
	isUserCorrectAnswer: false,
	isVideoSpeedControls: false,
	isLectureSwitchRestriction: false,
	quizPassingPercentage: 10,
	gamePassingPercentage: 10,
};

/**
 * Training Selection step (Simulation + Training campaigns).
 *
 * Layout matches the Figma:
 *  - a grid of selectable course cards with Filter / Single-Multiple / Search,
 *  - a "Configure Training" modal with two tabs:
 *      • Course Settings      — selected courses, each with Configure / Remove
 *      • Configure Email Template — Send Course / Completion / Quiz-fail dropdowns
 * The selected training is pushed up to the wizard as a `training` object.
 */
export default class TrainingSelectionStep extends Component {
	constructor(props) {
		super(props);
		const t = props.training || {};
		this.coursesCancel = null;
		this.templatesCancel = null;
		this.state = {
			courseLoader: false,
			lmsCourses: [],
			count: 0,
			imageURL: "",
			previewURL: "",
			courseName: {}, // { id: {name, logo} }
			// selection
			trainingAssignmentMode: t.trainingAssignmentMode || "single",
			selectedCourses: Array.isArray(t.selectedCourses) ? [...t.selectedCourses] : [],
			options: t.selectedOptions ? { ...t.selectedOptions } : {},
			// paging / search / filters
			limit: 12,
			activePage: 1,
			search: "",
			filters: { courseType: "", languageId: "", difficulty: "", complianceIds: [], topicIds: [] },
			filterPanelOpen: false,
			filterTab: "courseType",
			filterDraft: { courseType: "", languageId: "", difficulty: "", complianceIds: [], topicIds: [] },
			filterOptions: {
				// courseType is a fixed UI list (not data-driven). Lite/Advanced are
				// presentation labels that both route to the SCORM source and return
				// the same courses; Generic routes to the non-SCORM source.
				courseTypes: ["Generic", "Scorm Lite", "Scorm Advanced"],
				languages: [],
				difficulties: ["Beginner", "Intermediate", "Advanced"],
				compliances: [],
				topics: [],
			},
			// Configure Training modal
			showConfigureModal: false,
			configureTab: "course", // 'course' | 'email'
			emailTemplateTabVisited: false,
			// per-course settings modal
			showCourseSettings: false,
			settingsCourseId: null,
			settingsOptions: {},
			// email templates
			templates: {},
			sendCourseTemplateId: t.sendCourseTemplateId || "",
			courseCompletionTemplateId: t.courseCompletionTemplateId || "",
			courseQuizFailReminderTemplateId: t.courseQuizFailReminderTemplateId || "",
			// email preview
			showPreview: false,
			previewHtml: null,
		};
	}

	componentDidMount() {
		this.loadCourseTemplates();
		this.loadCourses();
	}

	componentWillUnmount() {
		if (this.coursesCancel) this.coursesCancel();
		if (this.templatesCancel) this.templatesCancel();
	}

	loadCourseTemplates = () => {
		const source = getCancelSource();
		if (source) this.templatesCancel = source.cancel;
		axios
			.get("/api/lms/template-list-threatcop", { cancelToken: source ? source.token : undefined })
			.then((res) => this.setState({ templates: res.data.templates || {} }))
			.catch((err) => { if (!axiosLib.isCancel(err)) apiError(err); });
	};

	loadCourses = () => {
		if (this.coursesCancel) this.coursesCancel();
		const source = getCancelSource();
		if (source) this.coursesCancel = source.cancel;
		const { filters } = this.state;
		/* Filter options come from this same API (no separate endpoint). Only an
		   UNFILTERED request reflects everything Krademy has, so capture options
		   from those responses only — a filtered response (e.g. courseType=Scorm)
		   would otherwise narrow the dropdowns to the current result set. */
		const isUnfilteredRequest = !this.state.search && !filters.courseType && !filters.languageId
			&& !filters.difficulty && !filters.complianceIds.length && !filters.topicIds.length;
		this.setState({ courseLoader: true });
		axios
			.get("/api/lms/course-list", {
				params: {
					limit: this.state.limit,
					page: this.state.activePage,
					search: this.state.search,
					courseType: filters.courseType || undefined,
					languageIds: filters.languageId || undefined,
					difficulty: filters.difficulty || undefined,
					complianceIds: filters.complianceIds.length ? filters.complianceIds.join(",") : undefined,
					topicIds: filters.topicIds.length ? filters.topicIds.join(",") : undefined,
				},
				cancelToken: source ? source.token : undefined,
			})
			.then((res) => {
				const courseName = { ...this.state.courseName };
				(res.data.courses || []).forEach((c) => {
					courseName[c.id] = { name: c.name, logo: c.logo };
				});
				this.setState({
					courseLoader: false,
					lmsCourses: res.data.courses || [],
					count: res.data.count || 0,
					imageURL: res.data.imageURL || "",
					previewURL: res.data.previewURL || "",
					courseName,
					...(res.data.filterOptions && isUnfilteredRequest ? {
						filterOptions: {
							// courseType is a fixed UI list (Generic / Scorm Lite / Scorm
							// Advanced) — never overwrite it from the API, which only
							// reports the source(s) that returned courses.
							courseTypes: this.state.filterOptions.courseTypes,
							languages: res.data.filterOptions.languages || [],
							difficulties: res.data.filterOptions.difficulties || this.state.filterOptions.difficulties,
							compliances: res.data.filterOptions.compliances || [],
							topics: res.data.filterOptions.topics || [],
						}
					} : {}),
				});
			})
			.catch((err) => {
				if (!axiosLib.isCancel(err)) {
					this.setState({ courseLoader: false });
					apiError(err);
				}
			});
	};

	handleCoursePreview = (courseId) => {
		if (!courseId || !this.state.previewURL) return;
		document.body.style.cursor = "wait";
		axios
			.get("/api/lms", {
				params: { url: `${this.state.previewURL}${courseId}?r=Sw4s56g` },
			})
			.then((res) => {
				const allowedBaseURL = new URL(this.state.previewURL);
				const url = new URL(res.data.url);

				if (url.origin !== allowedBaseURL.origin) {
					swalAlert("Security Alert", "Invalid redirect URL detected!", "error");
					return;
				}

				const redirectUrl = url.searchParams.get("url");
				if (redirectUrl) {
					try {
						const parsedRedirectUrl = new URL(redirectUrl, allowedBaseURL);
						if (parsedRedirectUrl.origin !== allowedBaseURL.origin) {
							swalAlert("Security Alert", "Invalid redirect target detected!", "error");
							return;
						}
					} catch (error) {
						swalAlert("Security Alert", "Invalid redirect target detected!", "error");
						return;
					}
				}

				window.open(res.data.url, "_blank");
			})
			.catch((err) => apiError(err))
			.finally(() => {
				document.body.style.cursor = "";
			});
	};

	handleModeChange = (mode) => {
		this.setState((prev) => {
			let selectedCourses = prev.selectedCourses;
			if (mode === "single" && selectedCourses.length > 1) selectedCourses = [selectedCourses[0]];
			return { trainingAssignmentMode: mode, selectedCourses };
		}, this.pushUpdate);
	};

	handleCourseToggle = (courseId) => {
		this.setState((prev) => {
			const selected = [...prev.selectedCourses];
			const idx = selected.indexOf(courseId);
			if (idx > -1) {
				selected.splice(idx, 1);
			} else if (prev.trainingAssignmentMode === "single") {
				return { selectedCourses: [courseId] };
			} else {
				selected.push(courseId);
			}
			return { selectedCourses: selected };
		}, this.pushUpdate);
	};

	handleCourseRemove = (courseId) => {
		this.setState((prev) => ({
			selectedCourses: prev.selectedCourses.filter((id) => id !== courseId),
		}), this.pushUpdate);
	};

	openCourseSettings = (courseId) => {
		const existing = this.state.options[courseId] || { ...DEFAULT_COURSE_OPTIONS };
		this.setState({ showCourseSettings: true, settingsCourseId: courseId, settingsOptions: existing });
	};

	setCourseOptions = (courseId, settings) => {
		this.setState((prev) => ({ options: { ...prev.options, [courseId]: settings } }), this.pushUpdate);
	};

	setFilterTab = (filterTab) => this.setState({ filterTab });

	handleDraftFilterChange = (key, value) => {
		this.setState((prev) => ({ filterDraft: { ...prev.filterDraft, [key]: value } }));
	};

	handleDraftMultiFilterToggle = (key, value) => {
		this.setState((prev) => {
			const arr = prev.filterDraft[key] || [];
			const next = arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
			return { filterDraft: { ...prev.filterDraft, [key]: next } };
		});
	};

	applyFilters = () => {
		this.setState((prev) => ({
			filters: { ...prev.filterDraft },
			activePage: 1,
			filterPanelOpen: false,
		}), this.loadCourses);
	};

	/* clear a single filter (string filters -> "", multi-select -> []) */
	removeFilter = (key) => {
		const emptyValue = Array.isArray(this.state.filters[key]) ? [] : "";
		this.setState((prev) => ({ filters: { ...prev.filters, [key]: emptyValue }, activePage: 1 }), this.loadCourses);
	};

	clearAllFilters = () => {
		this.setState({
			filters: { courseType: "", languageId: "", difficulty: "", complianceIds: [], topicIds: [] },
			activePage: 1,
		}, this.loadCourses);
	};

	/* build the list of active filters as { key, label } for the chips row */
	getActiveFilters = () => {
		const { filters, filterOptions } = this.state;
		const chips = [];
		if (filters.courseType) chips.push({ key: "courseType", label: `Course Type: ${filters.courseType}` });
		if (filters.languageId) {
			const lang = (filterOptions.languages || []).find((l) => String(l.id) === String(filters.languageId));
			chips.push({ key: "languageId", label: `Language: ${lang ? lang.name : filters.languageId}` });
		}
		if (filters.difficulty) chips.push({ key: "difficulty", label: `Difficulty: ${filters.difficulty}` });
		if (filters.complianceIds.length) chips.push({ key: "complianceIds", label: `Compliance: ${filters.complianceIds.length}` });
		if (filters.topicIds.length) chips.push({ key: "topicIds", label: `Topics: ${filters.topicIds.length}` });
		return chips;
	};

	renderActiveFilters() {
		const chips = this.getActiveFilters();
		if (!chips.length) return null;
		return (
			<div className="training-active-filters">
				{chips.map((chip) => (
					<span key={chip.key} className="training-filter-chip">
						{chip.label}
						<button type="button" className="training-filter-chip-remove" aria-label={`Remove ${chip.label}`}
							onClick={() => this.removeFilter(chip.key)}>×</button>
					</span>
				))}
				<button type="button" className="training-filter-clear-all" onClick={this.clearAllFilters}>Clear all</button>
			</div>
		);
	}

	loadTemplatePreview = (templateType) => {
		const idMap = {
			course: this.state.sendCourseTemplateId,
			completion: this.state.courseCompletionTemplateId,
			quiz_fail: this.state.courseQuizFailReminderTemplateId,
		};
		axios
			.get("/api/lms/template-preview-threatcop", { params: { id: idMap[templateType] || 0, templateType } })
			.then((res) => this.setState({ showPreview: true, previewHtml: res.data.html }))
			.catch((err) => apiError(err));
	};

	setTemplateId = (key, value) => this.setState({ [key]: value }, this.pushUpdate);

	openConfigureModal = () => {
		if (!this.state.selectedCourses.length) {
			toast.error("Please select at least one training course first");
			return;
		}
		this.setState({ showConfigureModal: true, configureTab: "course" });
	};

	/** Every selected course must have its per-course settings configured. */
	allCoursesConfigured = () =>
		this.state.selectedCourses.length > 0 &&
		this.state.selectedCourses.every((id) => !!this.state.options[id]);

	/** Training is only "complete" once every course is configured AND the
		admin has reviewed/selected the email templates. */
	isTrainingFullyConfigured = () => this.allCoursesConfigured() && this.state.emailTemplateTabVisited;

	/** Modal "Next": block until course settings + email templates are done. */
	handleModalNext = () => {
		if (!this.allCoursesConfigured()) {
			// toast.error("Configure the settings for every selected course before continuing");
			this.setState({ configureTab: "course" });
			return;
		}
		if (!this.state.emailTemplateTabVisited) {
			// toast.error("Please review and select the email templates before continuing");
			this.setState({ configureTab: "email", emailTemplateTabVisited: true });
			return;
		}
		this.pushUpdate();
		this.setState({ showConfigureModal: false });
		if (this.props.onProceed) this.props.onProceed();
	};

	/** Build the training object and hand it to the wizard. */
	pushUpdate = () => {
		if (!this.props.onUpdate) return;
		const selectedOptions = {};
		this.state.selectedCourses.forEach((courseId) => {
			selectedOptions[courseId] = this.state.options[courseId] || { ...DEFAULT_COURSE_OPTIONS };
		});
		this.props.onUpdate({
			trainingAssignmentMode: this.state.trainingAssignmentMode,
			selectedCourses: [...this.state.selectedCourses],
			selectedOptions,
			courseName: this.state.courseName,
			imageURL: this.state.imageURL,
			sendCourseTemplateId: this.state.sendCourseTemplateId || null,
			courseCompletionTemplateId: this.state.courseCompletionTemplateId || null,
			courseQuizFailReminderTemplateId: this.state.courseQuizFailReminderTemplateId || null,
			sendQuizTemplateId: null,
		});
	};

	validate = () => {
		if (!this.state.selectedCourses.length) {
			toast.error("Please select at least one training course");
			return false;
		}
		if (this.state.trainingAssignmentMode === "single" && this.state.selectedCourses.length > 1) {
			toast.error("Single mode allows only one training course");
			return false;
		}
		// Clicking the wizard's Continue opens the Configure Training modal and
		// requires course settings + email templates before advancing.
		if (!this.isTrainingFullyConfigured()) {
			this.setState({
				showConfigureModal: true,
				configureTab: this.allCoursesConfigured() ? "email" : "course",
				...(this.allCoursesConfigured() ? { emailTemplateTabVisited: true } : {}),
			});
			// toast.info("Please configure the course settings and email templates to continue");
			return false;
		}
		this.pushUpdate();
		return true;
	};

	renderFilterMenu() {
		const { filterDraft, filterOptions, filterTab } = this.state;
		const hasCompliances = filterOptions.compliances.length > 0;
		const hasTopics = filterOptions.topics.length > 0;
		return (
			<Dropdown
				className="category-filter-dropdown"
				show={this.state.filterPanelOpen}
				onToggle={(isOpen) => this.setState({
					filterPanelOpen: isOpen,
					...(isOpen ? { filterDraft: { ...this.state.filters } } : {}),
				})}
			>
				<Dropdown.Toggle variant="secondary" className="filter-select category-dropdown-toggle">
					Filters
				</Dropdown.Toggle>
				<Dropdown.Menu className="filter-panel-dropdown p-0" style={{ minWidth: "360px" }}>
					<div className="d-flex">
						<div className="filter-vertical-tabs border-right" style={{ width: "120px", minHeight: "220px" }}>
							<button type="button" className={`d-block w-100 vertical-tab-item text-left ${filterTab === "courseType" ? "active" : ""}`}
								onClick={() => this.setFilterTab("courseType")}>
								Course Type
							</button>
							<button type="button" className={`d-block w-100 vertical-tab-item text-left ${filterTab === "language" ? "active" : ""}`}
								onClick={() => this.setFilterTab("language")}>
								Language
							</button>
							<button type="button" className={`d-block w-100 vertical-tab-item text-left ${filterTab === "difficulty" ? "active" : ""}`}
								onClick={() => this.setFilterTab("difficulty")}>
								Difficulty
							</button>
							{hasCompliances && (
								<button type="button" className={`d-block w-100 vertical-tab-item text-left ${filterTab === "compliance" ? "active" : ""}`}
									onClick={() => this.setFilterTab("compliance")}>
									Compliance
								</button>
							)}
							{hasTopics && (
								<button type="button" className={`d-block w-100 vertical-tab-item text-left ${filterTab === "topics" ? "active" : ""}`}
									onClick={() => this.setFilterTab("topics")}>
									Topics
								</button>
							)}
						</div>
						<div className="filter-tab-content flex-grow-1" style={{ minHeight: "220px" }}>
							{filterTab === "courseType" && (
								<>
									<h6 className="mb-3 filter-heading">Select Course Type</h6>
									<div className="filter-content-wrapper">
										<Form.Control as="select" value={filterDraft.courseType} onChange={(e) => this.handleDraftFilterChange("courseType", e.target.value)}>
											<option value="">All</option>
											{filterOptions.courseTypes.map((c) => <option key={c} value={c}>{c}</option>)}
										</Form.Control>
									</div>
								</>
							)}
							{filterTab === "language" && (
								<>
									<h6 className="mb-3 filter-heading">Select Language</h6>
									<div className="filter-content-wrapper">
										<Form.Control as="select" value={filterDraft.languageId} onChange={(e) => this.handleDraftFilterChange("languageId", e.target.value)}>
											<option value="">All</option>
											{filterOptions.languages.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
										</Form.Control>
									</div>
								</>
							)}
							{filterTab === "difficulty" && (
								<>
									<h6 className="mb-3 filter-heading">Select Difficulty</h6>
									<div className="filter-content-wrapper">
										<Form.Control as="select" value={filterDraft.difficulty} onChange={(e) => this.handleDraftFilterChange("difficulty", e.target.value)}>
											<option value="">All</option>
											{filterOptions.difficulties.map((d) => <option key={d} value={d}>{d}</option>)}
										</Form.Control>
									</div>
								</>
							)}
							{filterTab === "compliance" && hasCompliances && (
								<>
									<h6 className="mb-3 filter-heading">Select Compliance</h6>
									<div className="filter-content-wrapper">
										{filterOptions.compliances.map((c) => (
											<Form.Check key={c.id} type="checkbox" label={c.name}
												checked={filterDraft.complianceIds.includes(String(c.id))}
												onChange={() => this.handleDraftMultiFilterToggle("complianceIds", String(c.id))} />
										))}
									</div>
								</>
							)}
							{filterTab === "topics" && hasTopics && (
								<>
									<h6 className="mb-3 filter-heading">Select Topics</h6>
									<div className="filter-content-wrapper">
										{filterOptions.topics.map((t) => (
											<Form.Check key={t.id} type="checkbox" label={t.name}
												checked={filterDraft.topicIds.includes(String(t.id))}
												onChange={() => this.handleDraftMultiFilterToggle("topicIds", String(t.id))} />
										))}
									</div>
								</>
							)}
						</div>
					</div>
					<div className="filter-panel-footer p-2 border-top d-flex justify-content-end">
						<Button variant="primary" size="md" onClick={this.applyFilters}>Apply</Button>
					</div>
				</Dropdown.Menu>
			</Dropdown>
		);
	}

	renderConfigureModal() {
		const { configureTab, selectedCourses, options, imageURL, courseName, templates } = this.state;
		return (
			<Modal
				show={this.state.showConfigureModal}
				onHide={() => this.setState({ showConfigureModal: false })}
				centered
				// dialogClassName="configure-training-dialog"
				className="commonModal"
				size="lg"
			>
				<Modal.Header closeButton>
					<div>
						<h5 className="mb-0">Configure Training</h5>
						<p className="mb-0 text-muted" style={{ fontSize: "13px" }}>Define your training settings.</p>
					</div>
				</Modal.Header>
				<Modal.Body>
					<div className="configure-training-tabs">
						<button
							type="button"
							className={`configure-training-tab ${configureTab === "course" ? "active" : ""}`}
							onClick={() => this.setState({ configureTab: "course" })}
						>
							Course Settings
						</button>
						<button
							type="button"
							className={`configure-training-tab ${configureTab === "email" ? "active" : ""}`}
							onClick={() => this.setState({ configureTab: "email", emailTemplateTabVisited: true })}
						>
							Configure Email Template
						</button>
					</div>

					{configureTab === "course" ? (
						<div className="tableContainerWrapInner" style={{ overflowX: "auto" }}>
							<Table responsive className="mb-0 configure-training-table">
								<thead>
									<tr>
										<th>Title</th>
										<th className="text-center">Course Settings</th>
										<th className="text-center">Action</th>
									</tr>
								</thead>
								<tbody>
									{selectedCourses.length === 0 ? (
										<tr><td colSpan="3" className="text-center text-muted py-4">No courses selected</td></tr>
									) : selectedCourses.map((courseId) => {
										const isConfigured = !!options[courseId];
										return (
											<tr key={courseId}>
												<td>
													<div className="d-flex align-items-center">
														<Image src={imageURL + (courseName[courseId]?.logo || "")} alt="" height="34px" width="34px"
															className="mr-2" onError={(e) => { e.target.src = simulationandtrain; }} />
														<span>{courseName[courseId]?.name || `Course #${courseId}`}</span>
													</div>
												</td>
												<td className="text-center">
													<button type="button" className={`configure-setting-btn ${isConfigured ? "configured" : ""}`} onClick={() => this.openCourseSettings(courseId)}>
														<PiWarning /> {isConfigured ? "Configured" : "Configure Setting"}
													</button>
												</td>
												<td className="text-center">
													<button type="button" className="configure-remove-btn" onClick={() => this.handleCourseRemove(courseId)}>⊖ Remove</button>
												</td>
											</tr>
										);
									})}
								</tbody>
							</Table>
						</div>
					) : (
						<>
							<div className="alert alert-primary" style={{ fontSize: "12px", padding: "7px 10px" }}>
								<b>Note:</b> You can choose from the custom templates you've created in the TLMS portal or use the default templates provided.
							</div>
							<div className="tableContainerWrapInner" style={{ overflowX: "auto" }}>
								<Table responsive className="mb-0 configure-training-table">
									<thead>
										<tr>
											<th style={{ width: "40%" }}>Template</th>
											<th>Select Template</th>
											<th className="text-center">Preview</th>
											</tr>
									</thead>
									<tbody>
										<tr>
											<td>Send Course</td>
											<td>
												<Form.Control as="select" value={this.state.sendCourseTemplateId} onChange={(e) => this.setTemplateId("sendCourseTemplateId", e.target.value)}>
													{(templates.course || []).map((t, k) => t && <option key={"c" + k} value={t.id}>{t.name}</option>)}
												</Form.Control>
											</td>
											<td className="text-center setlinkColor" onClick={() => this.loadTemplatePreview("course")}><a href="#" onClick={(e) => e.preventDefault()}>view</a></td>
										</tr>
										<tr>
											<td>Course Completion</td>
											<td>
												<Form.Control as="select" value={this.state.courseCompletionTemplateId} onChange={(e) => this.setTemplateId("courseCompletionTemplateId", e.target.value)}>
													{(templates.completion || []).map((t, k) => t && <option key={"cc" + k} value={t.id}>{t.name}</option>)}
												</Form.Control>
											</td>
											<td className="text-center setlinkColor" onClick={() => this.loadTemplatePreview("completion")}><a href="#" onClick={(e) => e.preventDefault()}>view</a></td>
										</tr>
										<tr>
											<td>Course Quiz Fail Reminder</td>
											<td>
												<Form.Control as="select" value={this.state.courseQuizFailReminderTemplateId} onChange={(e) => this.setTemplateId("courseQuizFailReminderTemplateId", e.target.value)}>
													{(templates.quiz_fail || []).map((t, k) => t && <option key={"qf" + k} value={t.id}>{t.name}</option>)}
												</Form.Control>
											</td>
											<td className="text-center setlinkColor" onClick={() => this.loadTemplatePreview("quiz_fail")}><a href="#" onClick={(e) => e.preventDefault()}>view</a></td>
										</tr>
									</tbody>
								</Table>
							</div>
						</>
					)}
				</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" onClick={() => this.setState({ showConfigureModal: false })}>Cancel</Button>
					<Button variant="success" onClick={this.handleModalNext}> {configureTab === "email" ? "Save" : "Next"}</Button>
				</Modal.Footer>
			</Modal>
		);
	}

	render() {
		const { lmsCourses, courseLoader, selectedCourses, trainingAssignmentMode, imageURL } = this.state;

		return (
			<div className="step-content-wrapper">
				<div className="step-header">
					<h4>Training Selection</h4>
					<p>Choose a training course to initiate your simulation.</p>
				</div>

				{/* Toolbar: Filter | Single/Multiple | Search | Configure Training */}
				<div className="template-selection-filters">
					<div className="filter-row">
						<Form.Group className="filter-group">
							<Form.Label className="filter-label">Filter</Form.Label>
							{this.renderFilterMenu()}
						</Form.Group>
						<div className="filter-separator" />
						<Form.Group className="filter-group">
							<Form.Label className="template-mode-label">Training Assignment</Form.Label>
							<div className="template-mode-options">
								<div className={`template-mode-option ${trainingAssignmentMode === "single" ? "selected" : ""}`} onClick={() => this.handleModeChange("single")}>
									<Form.Check type="radio" name="trainingMode" onChange={() => this.handleModeChange("single")} checked={trainingAssignmentMode === "single"}
										label={<span className="template-mode-label-wrap">Single <OverlayTrigger placement="top" overlay={<Tooltip>Assign one training course.</Tooltip>}><PiInfo className="template-mode-info-icon" /></OverlayTrigger></span>} />
								</div>
								<div className="filter-separator-in" />
								<div className={`template-mode-option ${trainingAssignmentMode === "multiple" ? "selected" : ""}`} onClick={() => this.handleModeChange("multiple")}>
									<Form.Check type="radio" name="trainingMode" onChange={() => this.handleModeChange("multiple")} checked={trainingAssignmentMode === "multiple"}
										label={<span className="template-mode-label-wrap">Multiple <OverlayTrigger placement="top" overlay={<Tooltip>Assign multiple training courses.</Tooltip>}><PiInfo className="template-mode-info-icon" /></OverlayTrigger></span>} />
								</div>
							</div>
						</Form.Group>
						<Form.Group className="template-search-group">
							<Form.Control type="search" className="template-search-input" placeholder="Search"
								value={this.state.search}
								onChange={(e) => this.setState({ search: e.target.value, activePage: 1 }, this.loadCourses)} />
						</Form.Group>
					</div>
				</div>

				{/* Active filter chips (removable) */}
				{this.renderActiveFilters()}

				{/* Configure Training trigger */}
				{/* <div className="d-flex justify-content-between align-items-center my-2">
					<span className="training-selected-count">
						{selectedCourses.length > 0 ? `${selectedCourses.length} course${selectedCourses.length > 1 ? "s" : ""} selected` : "No courses selected"}
					</span>
				</div> */}

				{/* Course card grid */}
				{courseLoader ? (
					<div className="template-loading">Loading courses...</div>
				) : (
					<div className="template-grid">
						{lmsCourses.length > 0 ? lmsCourses.map((course, index) => {
							const isSelected = selectedCourses.indexOf(course.id) > -1;
							const thumb = course.logo ? imageURL + course.logo : simulationandtrain;
							return (
								<div key={course.id || index} className={`template-card ${isSelected ? "selected" : ""}`} onClick={() => this.handleCourseToggle(course.id)}>
									<div className="template-card-image">
										<img src={thumb} alt={course.name || "Course"} className="img-fluid" onError={(e) => { e.target.src = simulationandtrain; }} />
										{course.courseType === "scorm" && <div className="template-simulation-badge">SCORM</div>}
										<div className="template-card-checkbox-wrapper">
											<Form.Check type="checkbox" checked={isSelected}
												onChange={(e) => { e.stopPropagation(); this.handleCourseToggle(course.id); }}
												onClick={(e) => e.stopPropagation()}
												className="template-card-checkbox-input" />
										</div>
									</div>
									<div className="template-card-content">
										<div className="template-card-title-row">
											<h6 className="template-card-title">{course.name}</h6>
											<PiEye className="template-view-icon" title="Preview course"
												onClick={(e) => { e.stopPropagation(); this.handleCoursePreview(course.id); }} />
										</div>
										<p className="template-card-date">{getDate(course.createdAt)}</p>
										<div className="template-card-tags">
											{course.courseType === "scorm"
												? <span className="template-tag-category">SCORM</span>
												: <span className="template-tag-category">Generic</span>}
											{typeof course.totalLecture !== "undefined" && (
												<span className="template-tag-attack">{course.totalLecture} Lectures</span>
											)}
										</div>
									</div>
								</div>
							);
						}) : (
							<div className="template-empty">No courses found</div>
						)}
					</div>
				)}
				{this.state.count > this.state.limit && !courseLoader && (
					<div className="pagination-Wrap mt-2">
						<Pagination itemClass="page-item" linkClass="page-link"
							activePage={this.state.activePage} itemsCountPerPage={this.state.limit}
							totalItemsCount={this.state.count}
							onChange={(pageNumber) => this.setState({ activePage: pageNumber }, this.loadCourses)} />
					</div>
				)}

				{this.renderConfigureModal()}

				<ConfiguredModal
					showModal={this.state.showCourseSettings}
					showHandle={() => this.setState({ showCourseSettings: false })}
					courseId={this.state.settingsCourseId}
					options={this.state.settingsOptions}
					setOptions={this.setCourseOptions}
				/>
				<ReplyModal
					buffer={this.state.previewHtml}
					show={this.state.showPreview}
					handleClose={() => this.setState({ showPreview: false })}
				/>
			</div>
		);
	}
}
